// Display units. Everything is stored and computed in metres and km/h; these convert for display
// and input. Altitude, distance and wind speed are chosen independently.

export type AltUnit = 'm' | 'ft';
export type DistUnit = 'km' | 'mi';
export type SpeedUnit = 'kmh' | 'kt' | 'ms' | 'mph';

export interface Units {
  alt: AltUnit;
  dist: DistUnit;
  speed: SpeedUnit;
}

export const METRIC: Units = { alt: 'm', dist: 'km', speed: 'kmh' };

const FT = 0.3048;
const MI = 1609.344;

/** Sensible defaults from the browser locale: US feet/miles/mph, UK feet/km/mph, else metric. */
export function unitsForLocale(lang: string | undefined): Units {
  const region = (lang ?? '').split(/[-_]/)[1]?.toUpperCase();
  if (region === 'US' || region === 'LR' || region === 'MM') return { alt: 'ft', dist: 'mi', speed: 'mph' };
  if (region === 'GB') return { alt: 'ft', dist: 'km', speed: 'mph' };
  return { ...METRIC };
}

// ---- altitude -------------------------------------------------------------------------------
export const altUnit = (u: Units) => u.alt;
export const mToAlt = (m: number, u: Units) => (u.alt === 'ft' ? m / FT : m);
export const altToM = (v: number, u: Units) => (u.alt === 'ft' ? v * FT : v);
/** Natural stepper increment for altitudes. */
export const altStep = (u: Units) => (u.alt === 'ft' ? 100 : 50);

export function fmtAlt(m: number, u: Units): string {
  if (!Number.isFinite(m)) return '–';
  return `${Math.round(mToAlt(m, u)).toLocaleString()} ${u.alt}`;
}

// ---- distance -------------------------------------------------------------------------------
/** Metres per displayed distance unit (for chart axes). */
export const distScale = (u: Units) => (u.dist === 'mi' ? MI : 1000);

export function fmtDist(m: number, u: Units): string {
  if (!Number.isFinite(m)) return '–';
  if (u.dist === 'mi') {
    const mi = m / MI;
    return mi < 0.2 ? `${Math.round(m / FT)} ft` : `${mi.toFixed(mi < 10 ? 2 : 1)} mi`;
  }
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
}

// ---- speed (stored in km/h) -----------------------------------------------------------------
const PER_KMH: Record<SpeedUnit, number> = { kmh: 1, kt: 1 / 1.852, ms: 1 / 3.6, mph: 1 / 1.609344 };
export const SPEED_LABEL: Record<SpeedUnit, string> = { kmh: 'km/h', kt: 'kt', ms: 'm/s', mph: 'mph' };

export const kmhToSpeed = (kmh: number, u: Units) => kmh * PER_KMH[u.speed];
export const speedToKmh = (v: number, u: Units) => v / PER_KMH[u.speed];
/** Stepper increment for speeds in the display unit. */
export const speedStep = (u: Units) => (u.speed === 'ms' ? 0.5 : 1);

export function fmtSpeed(kmh: number, u: Units): string {
  const v = kmhToSpeed(kmh, u);
  return `${u.speed === 'ms' ? (Math.round(v * 2) / 2).toString() : Math.round(v)} ${SPEED_LABEL[u.speed]}`;
}

// ---- misc -----------------------------------------------------------------------------------
export function fmtGr(gr: number | null): string {
  if (gr === null) return 'not flyable';
  if (!Number.isFinite(gr)) return 'not reachable';
  return `${gr.toFixed(1)}:1`;
}

const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
export function compass(deg: number): string {
  return POINTS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];
}

/** Old settings stored a single 'metric' | 'imperial' string. */
export function migrateUnits(v: unknown, fallback: Units): Units {
  if (v === 'imperial') return { alt: 'ft', dist: 'mi', speed: 'mph' };
  if (v === 'metric') return { ...METRIC };
  if (v && typeof v === 'object' && 'alt' in v && 'dist' in v && 'speed' in v) return v as Units;
  return fallback;
}
